import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, Panel, StatusPill } from "@/components/common";
import { api, errMsg, fmtMoney } from "@/lib/api";

const PLAN = { name: "", price: "", duration_days: 30, includes_contact_access: true, description: "", features: "", active: true };
const PKG = { name: "", credits: "", price: "", description: "", active: true };

const ItemDialog = ({ state, onClose, onSaved }) => {
  const [f, setF] = useState({});
  const isPlan = state?.type === "plans";
  useEffect(() => {
    if (state) {
      const base = isPlan ? PLAN : PKG;
      const it = state.item ? { ...base, ...state.item } : base;
      setF(isPlan ? { ...it, features: (it.features || []).join?.(", ") ?? it.features } : it);
    }
  }, [state, isPlan]);

  const submit = async (e) => {
    e.preventDefault();
    const body = { ...f, price: Number(f.price) };
    delete body.id; delete body.created_at;
    if (isPlan) {
      body.duration_days = Number(f.duration_days);
      body.features = String(f.features || "").split(",").map((s) => s.trim()).filter(Boolean);
    } else body.credits = Number(f.credits);
    try {
      if (state.item) await api.put(`/admin/${state.type}/${state.item.id}`, body);
      else await api.post(`/admin/${state.type}`, body);
      toast.success("Saved");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const inp = (k, label, type = "text") => (
    <div><Label>{label}</Label><Input type={type} required={k !== "description" && k !== "features"} className="mt-1.5" value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`item-form-${k}`} /></div>
  );

  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="item-dialog">
        <DialogHeader><DialogTitle className="font-display">{state?.item ? "Edit" : "New"} {isPlan ? "subscription plan" : "credit package"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {inp("name", "Name")}
          <div className="grid grid-cols-2 gap-4">{inp("price", "Price (INR)", "number")}{isPlan ? inp("duration_days", "Duration (days)", "number") : inp("credits", "Credits", "number")}</div>
          {inp("description", "Description")}
          {isPlan && inp("features", "Features (comma separated)")}
          {isPlan && <div className="flex items-center justify-between"><Label>Includes contact access</Label><Switch checked={!!f.includes_contact_access} onCheckedChange={(v) => setF({ ...f, includes_contact_access: v })} data-testid="item-form-contact-access" /></div>}
          <div className="flex items-center justify-between"><Label>Active (visible to agencies)</Label><Switch checked={!!f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="item-form-active" /></div>
          <Button type="submit" className="w-full" data-testid="item-form-submit">Save</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ItemList = ({ type, items, onEdit, onDelete }) => (
  <div className="divide-y">
    {items.map((it) => (
      <div key={it.id} className="flex items-center justify-between py-3 gap-3" data-testid={`${type}-item-${it.id}`}>
        <div className="min-w-0">
          <p className="font-medium flex items-center gap-2">{it.name}{!it.active && <StatusPill status="cancelled" />}</p>
          <p className="text-xs text-muted-foreground">{fmtMoney(it.price)} · {type === "plans" ? `${it.duration_days} days${it.includes_contact_access ? " · contact access" : ""}` : `${it.credits} credits`}</p>
        </div>
        <div className="flex gap-1">
          <Button size="icon" variant="ghost" onClick={() => onEdit(it)} data-testid={`${type}-edit-${it.id}`}><Pencil className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="text-destructive" onClick={() => onDelete(it)} data-testid={`${type}-delete-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
    ))}
    {items.length === 0 && <p className="text-sm text-muted-foreground py-4">None yet</p>}
  </div>
);

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [pkgs, setPkgs] = useState([]);
  const [dlg, setDlg] = useState(null);
  const load = () => {
    api.get("/admin/plans").then((r) => setPlans(r.data));
    api.get("/admin/packages").then((r) => setPkgs(r.data));
  };

  useEffect(() => {
    load();
  }, []);

  const del = async (type, it) => {
    if (!window.confirm(`Delete ${it.name}?`)) return;
    try {
      await api.delete(`/admin/${type}/${it.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const addBtn = (type, t) => <Button size="sm" variant="outline" onClick={() => setDlg({ type })} data-testid={`add-${type}-button`}><Plus className="h-4 w-4 mr-1" />{t}</Button>;
  return (
    <div>
      <PageHeader eyebrow="Catalog" title="Plans & credit packages" subtitle="Configure what agencies can buy." />
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Subscription plans" actions={addBtn("plans", "Add plan")}><ItemList type="plans" items={plans} onEdit={(item) => setDlg({ type: "plans", item })} onDelete={(it) => del("plans", it)} /></Panel>
        <Panel title="Credit packages" actions={addBtn("packages", "Add package")}><ItemList type="packages" items={pkgs} onEdit={(item) => setDlg({ type: "packages", item })} onDelete={(it) => del("packages", it)} /></Panel>
      </div>
      <ItemDialog state={dlg} onClose={() => setDlg(null)} onSaved={load} />
    </div>
  );
}
